/**
 * Phase 16 (F16 / P0 — May 2026) — Auto-Backup panel.
 *
 * Lives in the Voyage Detail header. Opens a modal listing every
 * voyage-scoped auto-backup snapshot taken before a save / sync / restore.
 * The user can pick one, confirm, and the backend `POST
 * /api/auto-backups/{id}/restore` rewinds the voyage to that point.
 *
 * Born from the P0 data-loss incident — the "Sync from library" click
 * that overwrote a day's worth of typed dishes / menus / times. The
 * sync handler is now non-destructive AND backed up to this collection,
 * but the user still needs a visible "undo my last save" button to feel
 * safe. This component is that button.
 *
 * Design notes
 * ------------
 * * Native `window.confirm` for the per-restore "are you sure" step.
 *   No extra Radix Dialog — the parent VoyageDetail page already has
 *   enough modal surface.
 * * Loads on every open (no cache) so the list reflects whatever the
 *   user just saved a half-second ago.
 * * After a restore: closes itself + calls the parent's `onRestored`
 *   so VoyageDetail / MealPlanEditor can re-pull state from server.
 * * Errors render inline at the bottom of the panel (no toast),
 *   because the user is already in a "fix the wrong thing" mindset.
 */
import { useState, useEffect } from "react";
import client from "@/apiClient";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, History } from "lucide-react";

// Compact relative + absolute timestamp like "12 min ago · 14:23 · 30 May".
function formatTs(ts) {
  if (!ts) return "";
  let d;
  try { d = new Date(ts); }
  catch { return ts; }
  if (isNaN(d.getTime())) return ts;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  let rel;
  if (diffSec < 60) rel = "just now";
  else if (diffSec < 3600) rel = `${Math.floor(diffSec / 60)} min ago`;
  else if (diffSec < 86400) rel = `${Math.floor(diffSec / 3600)} h ago`;
  else rel = `${Math.floor(diffSec / 86400)} d ago`;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const day = String(d.getDate());
  const mon = d.toLocaleString(undefined, { month: "short" });
  return `${rel} · ${hh}:${mm} · ${day} ${mon}`;
}

function reasonLabel(r) {
  if (r === "pre_sync") return "before Sync from library";
  if (r === "pre_restore") return "before previous restore";
  if (r === "user_save") return "user save";
  return r || "—";
}

export default function AutoBackupPanel({ voyageId, onRestored }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);  // backup_id being restored
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !voyageId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    client
      .get("/auto-backups", { params: { source_id: voyageId, limit: 50 } })
      .then((r) => {
        if (cancelled) return;
        setItems(Array.isArray(r.data) ? r.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Failed to load backups");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, voyageId]);

  const handleRestore = async (backup) => {
    const ok = window.confirm(
      `Restore this voyage to the state from\n${formatTs(backup.timestamp)} `
      + `(${reasonLabel(backup.reason)})?\n\n`
      + "A fresh auto-backup of the CURRENT state will be taken first, "
      + "so you can undo this restore from the same panel.",
    );
    if (!ok) return;
    setRestoring(backup.id);
    setError("");
    try {
      await client.post(`/auto-backups/${backup.id}/restore`, null,
                        { params: { source_id: voyageId } });
      setOpen(false);
      if (typeof onRestored === "function") onRestored();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="shrink-0"
        data-testid="auto-backup-open-btn"
        title="Restore this voyage to an earlier saved state"
      >
        <History className="h-4 w-4" />
        Backups
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="auto-backup-dialog"
        >
          <DialogHeader>
            <DialogTitle>Restore from auto-backup</DialogTitle>
            <DialogDescription>
              Every save and "Sync from library" click on this voyage is
              snapshotted automatically. Pick a point in time to rewind
              the whole voyage to that state. Newer than 50 backups are
              kept; older ones are pruned.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto rounded border border-slate-200">
            {loading && (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">
                No backups yet. The next save will create one.
              </div>
            )}
            {!loading && items.length > 0 && (
              <ul className="divide-y divide-slate-100">
                {items.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    data-testid={`auto-backup-row-${b.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800">
                        {formatTs(b.timestamp)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {reasonLabel(b.reason)}
                        {b.byte_size > 0 && (
                          <> · {(b.byte_size / 1024).toFixed(1)} KB</>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(b)}
                      disabled={restoring === b.id}
                      data-testid={`auto-backup-restore-${b.id}`}
                    >
                      {restoring === b.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Restore"
                      )}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {error && (
            <div
              className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700"
              data-testid="auto-backup-error"
            >
              {error}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
